import {ChangeDetectionStrategy, Component, Inject} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from "@angular/material/dialog";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {AdminService} from "../../../../_services/admin.service";
import {SnackbarService} from "../../../../_services/snackbar.service";
import {AuthService} from "../../../../_services/auth.service";
import {debounceTime, filter, of, switchMap, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {ConfirmDialogComponent} from "../../../../shared/confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../../../../_types/shared";
import {UploadSessionFormComponent} from "../../../upload-session-form/upload-session-form.component";
import {UserRole, UserWithStringRole} from "../../../../_types/user";
import {TimelinePermission} from "../../../../_types/memorybox";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

interface NewPermissionData {
    timeline_id?: number,
    profile?: { id: number },
    user?: { id: number },
    title?: string,
    currentPermissions?: TimelinePermission[]
    displayCurrentPermissions?: boolean
    filter?: any
    excludeAdmins?: boolean
}

@Component({
    selector: 'app-new-permission',
    templateUrl: './new-permission.component.html',
    styleUrl: './new-permission.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class NewPermissionComponent {
    permissionForm: FormGroup
    userSearchControl: FormControl = new FormControl();
    loadingUsers: boolean = false;
    isRaa: boolean = false;
    cannotCreateAccess: boolean = false;
    displayCurrentPermissions: boolean = false;
    filteredUsers: UserWithStringRole[] = [];
    currentPermissions: TimelinePermission[] = [];
    timeline_id: number | undefined;
    title: string = ''
    profile: { id: number } | undefined;
    permissions: { name: string, value: string }[] = [];
    selectedUser: UserWithStringRole | undefined;
    roles: UserRole[] = [];

    constructor(protected dialogRef: MatDialogRef<NewPermissionComponent>,
                @Inject(MAT_DIALOG_DATA) private data: NewPermissionData,
                private formBuilder: FormBuilder,
                private adminService: AdminService,
                private snackbarService: SnackbarService,
                public dialog: MatDialog,
                private authService: AuthService) {
        this.permissionForm = this.formBuilder.group({
            permission: ['', Validators.required],
            name: [''],
        });
        this.adminService.getUserRoles().subscribe({
            next: r => {
                this.roles = r;
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });

        this.authService.getPermissions().then((r): void => {
            this.permissions = r;
            this.permissions.push({
                value: 'No Permission', name: 'No Access'
            })
        });
        this.authService.getDynamicSettings().then((r): void => {
            this.isRaa = r.is_raa;
        });

        this.userSearchControl.valueChanges.pipe(
            debounceTime(300),
            filter(value => typeof value === 'string' && value.length > 2),
            tap((): boolean => this.loadingUsers = true),
            switchMap(value => {
                let filters: any = {
                    order_by: 'full_name',
                    direction: 'asc',
                    deactivated: false
                }

                if (data.excludeAdmins) {
                    const adminRole: UserRole | undefined = this.roles.find((x: UserRole): boolean => x.name === 'Admin');
                    if (adminRole) {
                        filters['excluded_roles'] = adminRole.id;
                    }
                }
                if (data.filter) {
                    filters[data.filter.name] = data.filter.value;
                }
                return this.adminService.getAllUsers(0, 100000, value, filters).pipe(
                    // Handle API errors gracefully without breaking the stream
                    catchError(e => {
                        this.snackbarService.openSnackBar(e, 'error');
                        return of({results: [] as UserWithStringRole[]});
                    })
                );
            })
        ).subscribe(r => {
            let users: UserWithStringRole[] = [];
            users = r.results.filter((item: UserWithStringRole, index: number): boolean => {
                return (r.results.indexOf(item) == index);
            });
            this.loadingUsers = false;
            this.filteredUsers = users
        });

        this.userSearchControl.valueChanges.pipe(
            takeUntilDestroyed()
        ).subscribe((value): void => {
            if (value !== null && typeof value === 'object' && 'id' in value) {
                const user: UserWithStringRole = value as UserWithStringRole;
                this.selectedUser = user;
                this.updatePermissionControl(user);
            } else {
                this.selectedUser = undefined;
            }
        });

        if (data.title) {
            this.title = data.title
        }
        if (data.timeline_id) {
            this.timeline_id = data.timeline_id
        }
        if (data.currentPermissions) {
            this.currentPermissions = data.currentPermissions
        }
        if (data.displayCurrentPermissions) {
            this.displayCurrentPermissions = data.displayCurrentPermissions
        }

        this.profile = data.profile || data.user;
    }

    selectUser(user: UserWithStringRole): void {
        this.selectedUser = user;
    }

    createPermission(): void {
        if (this.selectedUser && this.selectedUser.role == 'Birth Family') {
            const formValue = this.permissionForm.getRawValue();

            const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
                panelClass: 'mediumWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message: 'Are you sure you want to give ' + this.authService.getDisplayName(this.selectedUser, 'role') + ' "' + formValue.permission + '" permission for ' + this.title + '?',
                    title: 'Add permission',
                    showHeader: true
                }
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                if (resp) {
                    this.createPermissionCall()
                }
            });
        } else {
            this.createPermissionCall()
        }
    }

    createPermissionCall(): void {
        const selectedUser: UserWithStringRole | undefined = this.selectedUser ||
            (this.userSearchControl.value !== null && typeof this.userSearchControl.value === 'object' && 'id' in this.userSearchControl.value ?
                this.userSearchControl.value as UserWithStringRole : undefined);
        if (!this.timeline_id || !selectedUser?.id) return;
        const formValue = this.permissionForm.getRawValue();

        let body = {
            user_id: selectedUser.id,
            permission: formValue.permission,
            name: formValue.name
        }
        if (!this.isRaa) {
            delete body['name']
        }

        this.adminService.newPermission(this.timeline_id, body).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('New relationship created', 'success')
                this.dialogRef.close(r)
                this.adminService.getUserRoles().subscribe({
                    next: res => {
                        // check if new user permission needs to have upload session modal
                        const userRole: UserRole | undefined = res.find((x: UserRole): boolean => x.name == selectedUser.role);
                        if (userRole?.raa_is_limited) {
                            this.dialog.open(UploadSessionFormComponent, {
                                panelClass: 'mediumWindow',
                                data: {
                                    user: selectedUser.id,
                                    permission: r
                                }
                            });
                        }
                    }, error: error => {
                        this.snackbarService.openSnackBar(error, 'error')
                        console.error(error)
                    }
                });
            }, error: error => {
                let errorMsg: string = error;

                if (this.isRaa && error == 'You cannot add a child to their own memory box. This child is the owner of this memory box.') {
                    let isChild: boolean = false;
                    for (let role of this.roles) {
                        if (role.name == this.userSearchControl.value.role) {
                            isChild = role.role_type == 'young_person'
                            break;
                        }
                    }
                    if (isChild) {
                        errorMsg = 'You do not need to add this user to their own memory box, by default they cannot view their own memory box till they are 18 years old.'
                    } else {
                        errorMsg = 'You do not need to add this user to their own memory box, by default they can view their own memory box.'
                    }
                }
                this.snackbarService.openSnackBar(errorMsg, 'error')
            }
        });
    }

    displayFn = (user: UserWithStringRole): string => {
        if (user) {
            return this.authService.getDisplayName(
                user,
                !this.isRaa ? 'username' : undefined
            );
        }

        return '';
    };

    checkIfUserInArray(item: UserWithStringRole, otherArray: TimelinePermission[]): boolean {
        let i
        if (otherArray) {
            i = otherArray.findIndex((x: TimelinePermission): boolean => x.user.id === item.id);
        }
        return (i != null && i >= 0);
    }


    nameDisplay(user: UserWithStringRole): string {
        return this.authService.getDisplayName(user, this.isRaa ? 'reference_number' : 'email')
    }

    private isBirthFamilyBeingAddedToOwnMemoryBox(user: UserWithStringRole): boolean {
        return user.role === 'Birth Family' && user.id === this.profile?.id;
    }

    private updatePermissionControl(user: UserWithStringRole): void {
        this.permissionForm.controls['permission'].enable();

        this.cannotCreateAccess = false;

        if (this.isBirthFamilyBeingAddedToOwnMemoryBox(user)) {
            this.permissionForm.patchValue({permission: 'View Only'});
            this.permissionForm.controls['permission'].disable();
            return;
        }

        const userRole: UserRole | undefined = this.roles.find((x: UserRole): boolean => x.name === user.role);
        if (!userRole) {
            return;
        }

        this.permissionForm.patchValue({permission: userRole.default_permission})

        if (userRole.is_fixed && userRole.default_permission == 'No Permission') {
            this.cannotCreateAccess = true;
        }
        if (this.isRaa && userRole.is_fixed) {
            this.permissionForm.controls['permission'].disable();
        }
    }

}
